import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowUpRight,
  BellRing,
  CalendarClock,
  Check,
  CheckCircle2,
  FileCheck2,
  MessageSquareText,
  Phone,
  PlayCircle,
  Receipt,
  ShieldCheck,
  Sparkles,
  Star,
} from 'lucide-react';
import '../alt6.css';

const TICKER_ITEMS = [
  { icon: Phone, agent: 'Reception', text: 'New patient call answered · visit booked' },
  { icon: ShieldCheck, agent: 'Insurance', text: "Tomorrow's schedule fully verified" },
  { icon: CalendarClock, agent: 'Scheduling', text: '2:10 opening filled from the waitlist' },
  { icon: FileCheck2, agent: 'Claims', text: "Today's claims scrubbed & submitted" },
  { icon: Receipt, agent: 'EOB posting', text: '14 EOBs posted and reconciled' },
  { icon: BellRing, agent: 'Recall', text: '6 overdue patients back on the books' },
  { icon: MessageSquareText, agent: 'Billing', text: 'Balance reminders sent with pay links' },
];

const QUIZ_STEPS = [
  {
    key: 'role',
    title: 'What best describes you?',
    hint: 'So we can tailor the walkthrough.',
    options: [
      { value: 'Owner / dentist', label: 'Owner or dentist' },
      { value: 'Operations leader', label: 'Office manager or ops' },
      { value: 'Other', label: 'Something else' },
    ],
  },
  {
    key: 'providers',
    title: 'How many providers work at your practice?',
    hint: 'Dentists and hygienists, across all locations.',
    options: [
      { value: '1-2', label: '1–2 providers' },
      { value: '3-5', label: '3–5 providers' },
      { value: '6+', label: '6 or more' },
    ],
  },
  {
    key: 'pms',
    title: 'What system do you run on today?',
    hint: "We migrate your data over — nothing gets left behind.",
    options: [
      { value: 'Dentrix', label: 'Dentrix' },
      { value: 'Eaglesoft', label: 'Eaglesoft' },
      { value: 'Open Dental', label: 'Open Dental' },
      { value: 'Other / not sure', label: 'Something else' },
    ],
  },
  {
    key: 'headache',
    title: "What's eating your team's time?",
    hint: "We'll build the walkthrough around it.",
    options: [
      { value: 'Missed calls & scheduling', label: 'Missed calls & scheduling' },
      { value: 'Insurance, claims & billing', label: 'Insurance, claims & billing' },
      { value: 'Recall & empty chairs', label: 'Recall & empty chairs' },
      { value: 'All of it', label: 'Honestly? All of it' },
    ],
  },
];

function StartTicker() {
  const reduceMotion = useReducedMotion();
  const [tick, setTick] = useState(2);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const items = [];
  for (let i = 0; i < 3; i += 1) {
    const idx = (((tick - i) % TICKER_ITEMS.length) + TICKER_ITEMS.length) % TICKER_ITEMS.length;
    items.push({ ...TICKER_ITEMS[idx], key: tick - i, status: i === 0 ? 'working' : 'done' });
  }

  return (
    <div className="a6-sim a6-start-ticker" style={{ marginTop: 30 }} aria-hidden="true">
      <div className="a6-sim-chrome">
        <span className="a6-sim-dots">
          <span />
          <span />
          <span />
        </span>
        <span className="a6-sim-title">Asha OS · working right now</span>
        <span className="a6-sim-live">
          <span className="a6-sim-live-dot" />
          Live
        </span>
      </div>
      <div style={{ padding: 14 }}>
        <div className="a6-feed">
          <AnimatePresence initial={false} mode="popLayout">
            {items.map((item) => {
              const Icon = item.status === 'done' ? Check : item.icon;
              return (
                <motion.div
                  key={item.key}
                  layout
                  initial={reduceMotion ? false : { opacity: 0, y: -14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: 12 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                  className={`a6-feed-item is-${item.status}`}
                >
                  <span className="a6-feed-icon">
                    <Icon size={15} strokeWidth={2.4} />
                  </span>
                  <span>
                    <span className="a6-feed-agent">{item.agent}</span>
                    <br />
                    <span className="a6-feed-text">{item.text}</span>
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Alt6Start() {
  const [mode, setMode] = useState('landing'); // landing | quiz
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ firstName: '', lastName: '', phone: '', practiceName: '' });
  const [submitState, setSubmitState] = useState('idle');
  const [stickyVisible, setStickyVisible] = useState(false);
  const heroFormRef = useRef(null);
  const reduceMotion = useReducedMotion();

  const utmSuffix = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const utms = [];
    params.forEach((value, key) => {
      if (key.startsWith('utm_')) utms.push(`${key}=${value}`);
    });
    return utms.length ? ` · ${utms.join(' ')}` : '';
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'See your practice on autopilot | Asha';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 560);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [mode, stepIndex]);

  const startQuiz = (event) => {
    event.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError('Enter a valid email to get started.');
      return;
    }
    setEmailError('');
    setMode('quiz');
  };

  const answerStep = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    if (stepIndex === 0) {
      setMode('landing');
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContact((prev) => ({ ...prev, [name]: value }));
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setSubmitState('submitting');
    try {
      const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8011' : '');
      const response = await fetch(`${apiUrl}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: email.trim(),
          phone: contact.phone,
          practiceName: contact.practiceName,
          providers: answers.providers || '',
          role: answers.role || '',
          message: `Current PMS: ${answers.pms || '—'} · Biggest headache: ${answers.headache || '—'}${utmSuffix}`,
          referralSource: 'alt6-start',
        }),
      });
      if (!response.ok) throw new Error('Request failed');
      setSubmitState('success');
      setStepIndex(QUIZ_STEPS.length + 1);
    } catch (error) {
      setSubmitState('error');
    }
  };

  const totalScreens = QUIZ_STEPS.length + 1; // + contact screen
  const progress = Math.min(((stepIndex + 1) / totalScreens) * 100, 100);
  const isSuccess = submitState === 'success';
  const currentStep = QUIZ_STEPS[stepIndex];
  const onContactScreen = stepIndex === QUIZ_STEPS.length && !isSuccess;

  const slide = {
    initial: reduceMotion ? false : { opacity: 0, x: 44 },
    animate: { opacity: 1, x: 0 },
    exit: reduceMotion ? undefined : { opacity: 0, x: -44 },
    transition: { duration: 0.32, ease: [0.2, 0.7, 0.2, 1] },
  };

  return (
    <div className="alt6-root a6-start">
      <header className="a6-start-header">
        <span className="a6-wordmark">
          <span className="a6-wordmark-dot" aria-hidden="true" />
          Asha
        </span>
        <span className="a6-start-hipaa">
          <ShieldCheck size={13} strokeWidth={2.6} />
          HIPAA-compliant
        </span>
      </header>

      <main className="a6-start-main">
        {mode === 'landing' ? (
          <>
            <section className="a6-start-hero">
              <div className="a6-start-stars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={15} strokeWidth={0} />
                ))}
                Loved by top dental teams
              </div>
              <h1>
                AI that runs your <em>dental office</em>
              </h1>
              <p className="a6-start-sub">
                Asha answers every call, verifies insurance, files claims, posts EOBs,
                and fills your schedule — on autopilot, around the clock.
              </p>
              <form ref={heroFormRef} className="a6-start-form" onSubmit={startQuiz} noValidate>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="What's your work email?"
                  aria-label="Work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button type="submit" className="a6-btn a6-btn-primary">
                  See it in action
                  <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </form>
              {emailError ? <p className="a6-start-field-error">{emailError}</p> : null}
              <div className="a6-start-chips">
                {['24/7 call answering', 'Claims filed same day', 'Schedule always full'].map(
                  (chip) => (
                    <span className="a6-start-chip" key={chip}>
                      <Check size={13} strokeWidth={3} />
                      {chip}
                    </span>
                  ),
                )}
              </div>
              <StartTicker />
            </section>

            <section className="a6-start-section" style={{ background: 'var(--a6-blue-wash)' }}>
              <div className="a6-section-head is-centered" style={{ textAlign: 'center' }}>
                <h2 className="a6-h2">What the AI takes off your plate</h2>
              </div>
              <div className="a6-start-rows">
                {[
                  [Phone, 'Every call answered', '24/7, in multiple languages, booked straight into the schedule.'],
                  [ShieldCheck, 'Insurance verified', 'Eligibility and full benefit breakdowns before every visit.'],
                  [FileCheck2, 'Claims filed clean', 'Scrubbed against payer rules and submitted the day of service.'],
                  [Receipt, 'EOBs posted', 'Payments posted and reconciled — denials appealed, not shelved.'],
                  [BellRing, 'Patients brought back', 'Recall and unaccepted treatment get persistent, thoughtful follow-up.'],
                  [MessageSquareText, 'Balances collected', 'Friendly reminders with pay links, not surprise statements.'],
                ].map(([Icon, title, body]) => (
                  <div className="a6-start-row" key={title}>
                    <span className="a6-card-icon">
                      <Icon size={18} strokeWidth={2.2} />
                    </span>
                    <span>
                      <strong>{title}</strong>
                      <p>{body}</p>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="a6-start-section">
              <div className="a6-section-head is-centered" style={{ textAlign: 'center' }}>
                <h2 className="a6-h2">Up and running in about 4 weeks</h2>
              </div>
              <div className="a6-start-rows">
                {[
                  ['1', 'We migrate everything', 'Patients, charts, ledgers, and imaging come over in full — with a dedicated migration lead.'],
                  ['2', 'AI ramps up gradually', 'Autopilot takes on more work module by module, at the pace you set. You approve what needs approving.'],
                  ['3', 'Your team gets time back', 'Hours that went to phones, claims, and follow-up go back to patients — and going home on time.'],
                ].map(([num, title, body]) => (
                  <div className="a6-start-row" key={num}>
                    <span
                      className="a6-card-icon"
                      style={{ fontFamily: 'var(--a6-display)', fontWeight: 750, fontSize: 17 }}
                    >
                      {num}
                    </span>
                    <span>
                      <strong>{title}</strong>
                      <p>{body}</p>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="a6-start-section a6-start-cta-final" style={{ paddingBottom: 96 }}>
              <div className="a6-cta-band" style={{ padding: '52px 24px' }}>
                <h2 style={{ fontSize: 'clamp(26px, 6.5vw, 38px)' }}>
                  See your practice on autopilot.
                </h2>
                <p style={{ fontSize: 16 }}>
                  A 30-minute walkthrough, built around your workflows. No pressure, no
                  scripted deck.
                </p>
                <button
                  type="button"
                  className="a6-btn a6-btn-light"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setTimeout(() => heroFormRef.current?.querySelector('input')?.focus(), 450);
                  }}
                >
                  Get started
                  <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </div>
            </section>

            <div className={`a6-start-sticky${stickyVisible ? ' is-visible' : ''}`}>
              <button
                type="button"
                className="a6-btn a6-btn-primary"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => heroFormRef.current?.querySelector('input')?.focus(), 450);
                }}
              >
                See your practice on autopilot
                <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
              </button>
            </div>
          </>
        ) : (
          <div className="a6-quiz">
            {!isSuccess ? (
              <div className="a6-quiz-top">
                <button type="button" className="a6-quiz-back" onClick={goBack} aria-label="Go back">
                  <ArrowLeft size={17} strokeWidth={2.4} />
                </button>
                <div className="a6-quiz-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="a6-quiz-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="a6-quiz-count">
                  {Math.min(stepIndex + 1, totalScreens)}/{totalScreens}
                </span>
              </div>
            ) : null}

            <AnimatePresence mode="wait" initial={false}>
              {currentStep ? (
                <motion.div key={currentStep.key} {...slide}>
                  <h2>{currentStep.title}</h2>
                  <p className="a6-quiz-hint">{currentStep.hint}</p>
                  <div className="a6-quiz-options">
                    {currentStep.options.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className="a6-quiz-option"
                        onClick={() => answerStep(currentStep.key, opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="a6-quiz-reassure">
                    Takes 30 seconds · sets up a walkthrough built around your practice
                  </p>
                </motion.div>
              ) : null}

              {onContactScreen ? (
                <motion.div key="contact" {...slide}>
                  <h2>Where should we reach you?</h2>
                  <p className="a6-quiz-hint">
                    We'll text you to find a time that works — usually within one
                    business day.
                  </p>
                  <form onSubmit={submitLead}>
                    <div className="a6-form-grid">
                      <div className="a6-field">
                        <label htmlFor="a6s-firstName">First name</label>
                        <input
                          id="a6s-firstName"
                          name="firstName"
                          autoComplete="given-name"
                          required
                          value={contact.firstName}
                          onChange={handleContactChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6s-lastName">Last name</label>
                        <input
                          id="a6s-lastName"
                          name="lastName"
                          autoComplete="family-name"
                          required
                          value={contact.lastName}
                          onChange={handleContactChange}
                        />
                      </div>
                      <div className="a6-field is-full">
                        <label htmlFor="a6s-practiceName">Practice name</label>
                        <input
                          id="a6s-practiceName"
                          name="practiceName"
                          autoComplete="organization"
                          required
                          value={contact.practiceName}
                          onChange={handleContactChange}
                        />
                      </div>
                      <div className="a6-field is-full">
                        <label htmlFor="a6s-phone">Mobile number</label>
                        <input
                          id="a6s-phone"
                          name="phone"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          required
                          value={contact.phone}
                          onChange={handleContactChange}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="a6-btn a6-btn-primary"
                      disabled={submitState === 'submitting'}
                    >
                      {submitState === 'submitting' ? 'Sending…' : 'Book my walkthrough'}
                      <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
                    </button>
                    {submitState === 'error' ? (
                      <p className="a6-form-error" role="alert">
                        Something went wrong. Please try again.
                      </p>
                    ) : null}
                    <p className="a6-quiz-reassure">
                      <ShieldCheck size={12} strokeWidth={2.6} style={{ verticalAlign: '-1.5px', marginRight: 4 }} />
                      No spam, ever. We only use this to schedule your walkthrough.
                    </p>
                  </form>
                </motion.div>
              ) : null}

              {isSuccess ? (
                <motion.div key="success" {...slide} className="a6-quiz-success">
                  <span className="a6-quiz-success-icon">
                    <CheckCircle2 size={30} strokeWidth={2.2} />
                  </span>
                  <h2>You're all set{contact.firstName ? `, ${contact.firstName}` : ''}.</h2>
                  <p className="a6-quiz-hint" style={{ marginBottom: 0 }}>
                    We'll text {contact.phone || 'you'} within one business day to find a
                    time for your walkthrough.
                  </p>
                  <div className="a6-quiz-next">
                    {[
                      [CalendarClock, 'A 30-minute walkthrough with someone who builds the product'],
                      [PlayCircle, `Autopilot live — focused on ${answers.headache ? answers.headache.toLowerCase() : 'your workflows'}`],
                      [Sparkles, 'A migration plan from your current system'],
                    ].map(([Icon, text]) => (
                      <div className="a6-start-row" key={text}>
                        <span className="a6-card-icon">
                          <Icon size={18} strokeWidth={2.2} />
                        </span>
                        <p style={{ alignSelf: 'center' }}>{text}</p>
                      </div>
                    ))}
                  </div>
                  <p className="a6-quiz-reassure" style={{ marginTop: 26 }}>
                    Curious meanwhile? <Link to="/alt6" style={{ color: 'var(--a6-blue-deep)', fontWeight: 650 }}>Explore the platform →</Link>
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        )}
      </main>

      <footer style={{ padding: '20px 20px 28px', textAlign: 'center' }}>
        <p style={{ fontSize: 12.5, color: 'var(--a6-muted)' }}>
          © {new Date().getFullYear()} Asha Health · HIPAA-compliant by design ·{' '}
          <Link to="/alt6" style={{ color: 'var(--a6-muted)', textDecoration: 'underline' }}>
            asha.health
          </Link>
        </p>
      </footer>
    </div>
  );
}
